<script setup lang="ts">
/**
 * The Admiralty's news, from the CMS (`GET /api/news`, first four), as
 * picture cards: the post's painting on top in a thin brass frame, the tag and
 * date, the title and two lines of summary. A post without a picture shows the
 * harbour painting. Draws nothing when the CMS is down or empty. Opening a
 * card is the page's business (`open`).
 */
import { useI18n } from '@/i18n';
import type { NewsSummary } from '@/net/api';

import SectionShell from './SectionShell.vue';

defineProps<{ notices: readonly NewsSummary[] }>();
defineEmits<{ open: [notice: NewsSummary] }>();
const { t } = useI18n();

/** Stands in for a post the admin published without a picture. */
const FALLBACK_ART = '/art/loading-harbour.webp';

function dateOf(n: NewsSummary): string {
  const iso = n.publishedAt ?? n.createdAt;
  try {
    return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}
</script>

<template>
  <SectionShell v-if="notices.length" id="notices" icon="bell" :title="t('landing.notices.caption')" backdrop="/art/scenes/office.webp">
    <ul class="grid">
      <li v-for="(n, i) in notices" :key="n.id" data-slam="rise" :style="{ '--i': i }">
        <button type="button" class="card k-card" @click="$emit('open', n)">
          <span class="art"><img :src="n.imageUrl || FALLBACK_ART" alt="" width="480" height="270" loading="lazy" decoding="async" /></span>
          <span class="meta">
            <span class="tag">{{ n.tag }}</span>
            <span class="date">{{ dateOf(n) }}</span>
          </span>
          <span class="title">{{ n.title }}</span>
          <span class="summary">{{ n.summary }}</span>
        </button>
      </li>
    </ul>
  </SectionShell>
</template>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
  gap: 16px;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0;
  list-style: none;
}

.card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  height: 100%;
  color: inherit;
  text-align: left;
  cursor: pointer;
  touch-action: manipulation;
  transition: transform var(--dur) var(--ease), filter var(--dur) var(--ease);
}

.art {
  display: block;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 6px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.7), 0 0 0 2px rgba(185, 138, 58, 0.5);
}

.art img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 600ms var(--ease);
}

.meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  font-family: var(--font-game-body);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.tag {
  color: var(--kit-gold);
}

.date {
  color: #93a4b5;
}

.title {
  font-family: var(--font-game);
  font-size: 1.2rem;
  line-height: 1.15;
  color: #fff;
  text-transform: uppercase;
}

.summary {
  font-family: var(--font-game-body);
  font-size: 0.85rem;
  line-height: 1.4;
  color: #cfdbe6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@media (hover: hover) {
  .card:hover {
    transform: translateY(-2px);
    filter: brightness(1.08);
  }

  .card:hover .art img {
    transform: scale(1.04);
  }
}

@media (prefers-reduced-motion: reduce) {
  .card,
  .art img {
    transition: none;
  }
}
</style>
